import React, { useEffect, useState } from 'react';
import {
  Form, Input, Button, Select, DatePicker, message, Spin, Card, Row, Col, Popconfirm, Tag, Space, Switch, Modal, Flex
} from 'antd';
import Table from 'components/Common/Table';
import {
  SaveOutlined, UserOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, PlayCircleOutlined, BugOutlined, CheckSquareOutlined, RocketOutlined, StarOutlined, BookOutlined, ThunderboltOutlined, ExperimentOutlined, FlagOutlined, FireOutlined, BulbOutlined, ToolOutlined, SettingOutlined, SafetyCertificateOutlined, ApiOutlined, CodeOutlined, DatabaseOutlined, CloudOutlined, LockOutlined, FileTextOutlined, AuditOutlined, CopyOutlined, LinkOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

// Available icons for task types
const TASK_TYPE_ICONS = [
  { value: 'check-square', label: 'Check Square', icon: <CheckSquareOutlined /> },
  { value: 'bug', label: 'Bug', icon: <BugOutlined /> },
  { value: 'rocket', label: 'Rocket', icon: <RocketOutlined /> },
  { value: 'star', label: 'Star', icon: <StarOutlined /> },
  { value: 'book', label: 'Book', icon: <BookOutlined /> },
  { value: 'thunderbolt', label: 'Thunderbolt', icon: <ThunderboltOutlined /> },
  { value: 'experiment', label: 'Experiment', icon: <ExperimentOutlined /> },
  { value: 'flag', label: 'Flag', icon: <FlagOutlined /> },
  { value: 'fire', label: 'Fire', icon: <FireOutlined /> },
  { value: 'bulb', label: 'Bulb', icon: <BulbOutlined /> },
  { value: 'tool', label: 'Tool', icon: <ToolOutlined /> },
  { value: 'setting', label: 'Setting', icon: <SettingOutlined /> },
  { value: 'safety-certificate', label: 'Security', icon: <SafetyCertificateOutlined /> },
  { value: 'api', label: 'API', icon: <ApiOutlined /> },
  { value: 'code', label: 'Code', icon: <CodeOutlined /> },
  { value: 'database', label: 'Database', icon: <DatabaseOutlined /> },
  { value: 'cloud', label: 'Cloud', icon: <CloudOutlined /> },
  { value: 'lock', label: 'Lock', icon: <LockOutlined /> },
  { value: 'file-text', label: 'Document', icon: <FileTextOutlined /> },
  { value: 'audit', label: 'Audit', icon: <AuditOutlined /> },
];

// Helper to get icon component by name
const getIconByName = (iconName) => {
  const iconObj = TASK_TYPE_ICONS.find(i => i.value === iconName);
  return iconObj ? iconObj.icon : <CheckSquareOutlined />;
};

const ProjectForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [existingMembers, setExistingMembers] = useState([]);
  const [projectOwner, setProjectOwner] = useState(null);
  const [currentUserEmployee, setCurrentUserEmployee] = useState(null);

  // Task Statuses
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [statusForm] = Form.useForm();

  // Task Types
  const [taskTypes, setTaskTypes] = useState([]);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm] = Form.useForm();

  // Support Portal
  const [supportUrl, setSupportUrl] = useState(null);

  useEffect(() => {
    fetchEmployees();
    fetchCurrentUserEmployee();
    if (id) {
      fetchProject();
      fetchTaskStatuses();
      fetchTaskTypes();
    }
  }, [id]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/employee/employees/');
      setEmployees(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchCurrentUserEmployee = async () => {
    try {
      // Fetch the current user's employee record
      const response = await axios.get('/employee/employees/me/');
      if (response.data) {
        setCurrentUserEmployee(response.data.id);
      }
    } catch (error) {
      console.error('Error fetching current user employee:', error);
      // Fallback: try to find employee by user email
      if (user?.email) {
        try {
          const empResponse = await axios.get(`/employee/employees/?search=${user.email}`);
          const employees = empResponse.data.results || empResponse.data || [];
          if (employees.length > 0) {
            setCurrentUserEmployee(employees[0].id);
          }
        } catch (e) {
          console.error('Error fetching employee by email:', e);
        }
      }
    }
  };

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/project_board/projects/${id}/`);
      const project = response.data;

      // Check if user is owner - redirect non-owners to view page
      if (!project.is_owner) {
        message.warning('Only the project owner can edit this project.');
        navigate(`/projects/view/${id}`, { replace: true });
        return;
      }

      // Fetch project members
      const membersResponse = await axios.get(`/project_board/members/?project=${id}`);
      const members = membersResponse.data.results || membersResponse.data;
      setExistingMembers(members);
      setProjectOwner(project.owner);
      setSupportUrl(project.support_url);

      form.setFieldsValue({
        ...project,
        owner_name: project.owner_name, // Store owner_name for display
        start_date: project.start_date ? dayjs(project.start_date) : null,
        due_date: project.due_date ? dayjs(project.due_date) : null,
        members: members.map(m => m.user),
      });
    } catch (error) {
      message.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskStatuses = async () => {
    try {
      const response = await axios.get(`/project_board/statuses/?project=${id}`);
      setTaskStatuses(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching task statuses:', error);
    }
  };

  const fetchTaskTypes = async () => {
    try {
      const response = await axios.get(`/project_board/task-types/?project=${id}`);
      setTaskTypes(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching task types:', error);
    }
  };

  const onFinish = async (values) => {
    try {
      setSubmitting(true);
      const formData = {
        ...values,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
      };

      // Remove members from formData as it will be handled separately
      const selectedMembers = formData.members || [];
      delete formData.members;

      // Never send owner fields - owner is auto-assigned on create and immutable
      delete formData.owner;
      delete formData.owner_name;

      let savedProjectId = id;

      if (id) {
        await axios.put(`/project_board/projects/${id}/`, formData);
        message.success('Project updated successfully');
      } else {
        const response = await axios.post('/project_board/projects/', formData);
        savedProjectId = response.data.id;
        message.success('Project created successfully');
      }

      // Update project members
      await updateProjectMembers(savedProjectId, selectedMembers);

      navigate('/projects');
    } catch (error) {
      message.error(error.response?.data?.project_code?.[0] || 'Failed to save project');
      console.error('Error saving project:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const updateProjectMembers = async (projectId, memberUserIds) => {
    try {
      const existingUserIds = existingMembers.map(m => m.user);

      // Delete members that are no longer selected
      for (const member of existingMembers) {
        if (!memberUserIds.includes(member.user)) {
          await axios.delete(`/project_board/members/${member.id}/`);
        }
      }

      // Add new members
      for (const userId of memberUserIds) {
        if (!existingUserIds.includes(userId)) {
          await axios.post('/project_board/members/', {
            project: projectId,
            user: userId,
            role: 'MEMBER'
          });
        }
      }
    } catch (error) {
      console.error('Error updating project members:', error);
      message.warning('Project saved but there was an issue updating members');
    }
  };

  // Task Status Management
  const handleAddStatus = () => {
    setEditingStatus(null);
    statusForm.resetFields();
    setStatusModalVisible(true);
  };

  const handleEditStatus = (status) => {
    setEditingStatus(status);
    statusForm.setFieldsValue(status);
    setStatusModalVisible(true);
  };

  const handleDeleteStatus = async (statusId) => {
    try {
      await axios.delete(`/project_board/statuses/${statusId}/`);
      message.success('Status deleted successfully');
      fetchTaskStatuses();
    } catch (error) {
      message.error('Failed to delete status. It may be in use by tasks.');
    }
  };

  const handleStatusSubmit = async (values) => {
    try {
      if (editingStatus) {
        await axios.put(`/project_board/statuses/${editingStatus.id}/`, {
          ...values,
          project: id
        });
        message.success('Status updated successfully');
      } else {
        await axios.post('/project_board/statuses/', {
          ...values,
          project: id,
          order: taskStatuses.length + 1
        });
        message.success('Status created successfully');
      }
      setStatusModalVisible(false);
      fetchTaskStatuses();
    } catch (error) {
      message.error('Failed to save status');
    }
  };

  // Task Type Management
  const handleAddType = () => {
    setEditingType(null);
    typeForm.resetFields();
    setTypeModalVisible(true);
  };

  const handleEditType = (type) => {
    setEditingType(type);
    typeForm.setFieldsValue(type);
    setTypeModalVisible(true);
  };

  const handleDeleteType = async (typeId) => {
    try {
      await axios.delete(`/project_board/task-types/${typeId}/`);
      message.success('Task type deleted successfully');
      fetchTaskTypes();
    } catch (error) {
      message.error('Failed to delete task type. It may be in use by tasks.');
    }
  };

  const handleTypeSubmit = async (values) => {
    try {
      if (editingType) {
        await axios.put(`/project_board/task-types/${editingType.id}/`, {
          ...values,
          project: id
        });
        message.success('Task type updated successfully');
      } else {
        await axios.post('/project_board/task-types/', {
          ...values,
          project: id
        });
        message.success('Task type created successfully');
      }
      setTypeModalVisible(false);
      fetchTaskTypes();
    } catch (error) {
      message.error('Failed to save task type');
    }
  };

  const statusColumns = [
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Tag color={record.color}>{text}</Tag>
      ),
    },
    {
      title: 'Initial',
      dataIndex: 'is_initial',
      key: 'is_initial',
      width: 80,
      render: (isInitial) => (
        isInitial ? <PlayCircleOutlined style={{ color: '#1890ff' }} /> : null
      ),
    },
    {
      title: 'Completed',
      dataIndex: 'is_completed',
      key: 'is_completed',
      width: 100,
      render: (completed) => (
        completed ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null
      ),
    },
    {
      title: 'Task Count',
      dataIndex: 'task_count',
      key: 'task_count',
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditStatus(record)}
          />
          <Popconfirm
            title="Delete this status?"
            onConfirm={() => handleDeleteStatus(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const typeColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Tag color={record.color}>
          {getIconByName(record.icon)} {text}
        </Tag>
      ),
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: (iconName) => (
        <Space>
          {getIconByName(iconName)}
          <span style={{ fontSize: '12px', color: '#888' }}>{iconName}</span>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditType(record)}
          />
          <Popconfirm
            title="Delete this task type?"
            onConfirm={() => handleDeleteType(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      <h2>{id ? 'Edit Project' : 'Create New Project'}</h2>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Flex justify="end" gap="middle" style={{ margin: "10px auto" }}>
          <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />}>
            {id ? 'Update Project' : 'Create Project'}
          </Button>
          <Button onClick={() => navigate('/projects')}>
            Cancel
          </Button>
        </Flex>

        <Card title="Basic Information" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="project_code"
                label="Project Code"
                rules={[{ required: true, message: 'Please enter project code' }]}
                help={id ? "Project code cannot be changed" : "Enter a unique project code (e.g., PRJ-2025-0001)"}
              >
                <Input placeholder="e.g., PRJ-2025-0001" disabled={!!id} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Project Status"
                rules={[{ required: true }]}
                initialValue="PLANNING"
              >
                <Select placeholder="Select status">
                  <Option value="PLANNING">Planning</Option>
                  <Option value="IN_PROGRESS">In Progress</Option>
                  <Option value="ON_HOLD">On Hold</Option>
                  <Option value="COMPLETED">Completed</Option>
                  <Option value="CANCELLED">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="name"
            label="Project Name"
            rules={[{ required: true, message: 'Please enter project name' }]}
          >
            <Input placeholder="Enter project name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter project description" />
          </Form.Item>
        </Card>

        <Card title="Team & Schedule" style={{ marginBottom: 16 }}>
          <Form.Item
            label="Project Owner"
          >
            {id ? (
              <Input
                value={form.getFieldValue('owner_name') || 'Loading...'}
                disabled
                prefix={<UserOutlined />}
              />
            ) : (
              <Input
                value="You (automatically assigned)"
                disabled
                prefix={<UserOutlined />}
              />
            )}
          </Form.Item>

          <Form.Item
            name="members"
            label="Project Members"
            help={!id || currentUserEmployee === projectOwner || user?.is_superuser ? "Select employees who can access this project" : "Only the project owner can modify members"}
          >
            <Select
              mode="multiple"
              placeholder="Select project members"
              showSearch
              allowClear
              disabled={id && currentUserEmployee !== projectOwner && !user?.is_superuser}
              suffixIcon={<UserOutlined />}
              optionFilterProp="label"
            >
              {employees
                .filter(emp => emp.id !== (projectOwner || currentUserEmployee))
                .map(emp => (
                  <Option key={emp.user} value={emp.id} label={`${emp.user_name || ''} (${emp.employee_id || ''})`}>
                    {emp.user_name || '-'} ({emp.employee_id || '-'})
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="Start Date"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="due_date"
                label="Due Date"
                dependencies={['start_date']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue('start_date');
                      if (!value || !startDate) {
                        return Promise.resolve();
                      }
                      if (value.isBefore(startDate)) {
                        return Promise.reject(new Error('End Date cannot be earlier than Start Date'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {id && (
          <Card title="Support Portal Settings" style={{ marginBottom: 16 }}>
            <Form.Item
              name="is_support_enabled"
              label="Enable Support Portal"
              valuePropName="checked"
              help="Allow customers to submit support tickets for this project"
            >
              <Switch />
            </Form.Item>

            {supportUrl && (
              <Form.Item
                label="Support Portal URL"
                help="Share this URL with customers to allow them to submit support tickets"
              >
                <Input.Group compact>
                  <Input
                    style={{ width: 'calc(100% - 80px)' }}
                    value={supportUrl}
                    readOnly
                    prefix={<LinkOutlined />}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(supportUrl);
                      message.success('URL copied to clipboard!');
                    }}
                  >
                    Copy
                  </Button>
                </Input.Group>
              </Form.Item>
            )}

            <Form.Item
              name="support_public_line"
              label="Public Support Message"
              help="This message will be displayed on the support signup page for customers"
            >
              <TextArea
                rows={3}
                placeholder="Enter a public message for customers signing up for support (e.g., support hours, contact info, important notes)"
              />
            </Form.Item>
          </Card>
        )}

        {id && (
          <>
            <Card
              title="Task Statuses"
              style={{ marginBottom: 16 }}
              extra={
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddStatus}
                >
                  Add Status
                </Button>
              }
            >
              <Table
                dataSource={taskStatuses}
                columns={statusColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </Card>

            <Card
              title="Task Types"
              style={{ marginBottom: 16 }}
              extra={
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddType}
                >
                  Add Task Type
                </Button>
              }
            >
              <Table
                dataSource={taskTypes}
                columns={typeColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </Card>
          </>
        )}
      </Form>

      {/* Task Status Modal */}
      <Modal
        title={editingStatus ? 'Edit Task Status' : 'Add Task Status'}
        open={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusSubmit}
        >
          <Form.Item
            name="name"
            label="Status Name"
            rules={[{ required: true, message: 'Please enter status name' }]}
          >
            <Input placeholder="e.g., To Do, In Progress, Done" />
          </Form.Item>

          <Form.Item
            name="color"
            label="Color"
            initialValue="#1890ff"
          >
            <Input type="color" />
          </Form.Item>

          <Form.Item
            name="is_initial"
            label="Initial Status"
            valuePropName="checked"
            help="New tasks will be automatically assigned to this status. Only one per project."
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="is_completed"
            label="Completed Status"
            valuePropName="checked"
            help="Tasks in this status will be considered completed"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editingStatus ? 'Update' : 'Create'} Status
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Type Modal */}
      <Modal
        title={editingType ? 'Edit Task Type' : 'Add Task Type'}
        open={typeModalVisible}
        onCancel={() => setTypeModalVisible(false)}
        footer={null}
      >
        <Form
          form={typeForm}
          layout="vertical"
          onFinish={handleTypeSubmit}
        >
          <Form.Item
            name="name"
            label="Type Name"
            rules={[{ required: true, message: 'Please enter type name' }]}
          >
            <Input placeholder="e.g., Bug, Task, Feature" />
          </Form.Item>

          <Form.Item
            name="color"
            label="Color"
            initialValue="#1890ff"
          >
            <Input type="color" />
          </Form.Item>

          <Form.Item
            name="icon"
            label="Icon"
            initialValue="check-square"
          >
            <Select
              placeholder="Select an icon"
              optionLabelProp="label"
            >
              {TASK_TYPE_ICONS.map(item => (
                <Option key={item.value} value={item.value} label={<span>{item.icon} {item.label}</span>}>
                  <Space>
                    {item.icon}
                    <span>{item.label}</span>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editingType ? 'Update' : 'Create'} Task Type
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectForm;
